import { useContext, useMemo } from 'react';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminProfessionals } from '@platform/client';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { AdminProTeamMatchRecord } from '@platform/client/schemas';
import { ProjectLink } from '../projects/ProjectsAdmin';
import { NoRecordsFoundTabContent, ProfileTabContainer } from '../../components/ProfileComponents';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { OrganizationLink } from '../organizations/OrganizationsAdmin';
import { TeamLink } from '../teams/TeamsResultsTab';
import { TeamMatchLink } from '../teams-matching/TeamsMatchAdmin';

const columnHelper = createColumnHelper<AdminProTeamMatchRecord>();
export const TeamMatchingTabContent = ({ professionalId }: { professionalId: number }) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const {
    data: teamMatchData,
    isSuccess,
    isLoading,
    isError,
  } = AdminProfessionals.useGetProsTeamMatchingData({
    user_id: professionalId,
    page: adminQueryOptionsV2.page,
    limit: adminQueryOptionsV2.limit,
    sort: adminQueryOptionsV2.sort,
  });
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('discoveryId', {
          header: 'Id',
          cell: (info) => <TeamMatchLink discoveryId={info.getValue()} />,
          size: 5,
        }),
        columnHelper.accessor('organizationName', {
          header: 'Organization',
          cell: ({ row }) => (
            <OrganizationLink
              organizationId={row.original.organizationId}
              organizationName={row.original.organizationName}
            />
          ),
        }),
        columnHelper.accessor('projectName', {
          header: 'Project',
          cell: ({ row }) => (
            <ProjectLink
              projectId={row.original.projectId}
              projectName={row.original.projectName}
            />
          ),
          size: 300,
        }),
        columnHelper.accessor('teamId', {
          header: 'Team',
          cell: ({ row }) => (
            <TeamLink teamId={row.original.teamId} cohortId={row.original.cohortId} />
          ),
          size: 10,
        }),
        columnHelper.accessor('myProjectsStatus', {
          header: 'Status',
          cell: (info) => info.getValue(),
          size: 10,
        }),
        columnHelper.accessor('myProjectsStatusDate', {
          header: 'Status date',
          cell: (info) => formatDateStr(info.getValue()),
          size: 150,
        }),
      ] as ColumnDef<AdminProTeamMatchRecord>[],
    [],
  );

  return (
    <ProfileTabContainer>
      {isSuccess && teamMatchData?.teamMatchRecords.length === 0 ?
        <NoRecordsFoundTabContent recordType='professional' />
      : <AdminTable
          isError={isError}
          isLoading={isLoading}
          isSuccess={isSuccess}
          columns={columns}
          data={teamMatchData?.teamMatchRecords || []}
          isSimple
          filteredCount={teamMatchData?.totalCount ?? 0}
        />
      }
    </ProfileTabContainer>
  );
};
